import { formatCompactNumber } from '@/lib/utils';
import type { GrowthPoint } from './growth-model';

export const growthUserCounts = (
  point: GrowthPoint,
  visible: { signups: boolean; newAppUsers: boolean },
  goalLabel = 'Signups',
) => [
  ...(visible.newAppUsers && (point.newAppUsers ?? 0) > 0
    ? [
        {
          key: 'newAppUsers' as const,
          label: 'New app users',
          value: point.newAppUsers!,
        },
      ]
    : []),
  ...(visible.signups && (point.signups ?? 0) > 0
    ? [{ key: 'signups' as const, label: goalLabel, value: point.signups! }]
    : []),
];

export const growthUserLabelWidth = (
  counts: ReturnType<typeof growthUserCounts>,
) =>
  Math.max(
    0,
    ...counts.map((count) => formatCompactNumber(count.value).length * 8 + 14),
  );

/** Enough room for every count without widening ordinary desktop timelines. */
export const growthLabelPlotWidth = (
  points: GrowthPoint[],
  visible: {
    signups: boolean;
    newAppUsers: boolean;
    paid: boolean;
    revenue: boolean;
  },
) => {
  let labelWidth = 0;
  for (const point of points) {
    labelWidth = Math.max(
      labelWidth,
      growthUserLabelWidth(growthUserCounts(point, visible)),
    );
    const bar = visible.revenue
      ? point.recordedRevenueUsd
      : visible.paid
        ? point.newPaid
        : null;
    if (bar !== null && bar > 0) {
      const text = visible.revenue
        ? new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: 'USD',
          }).format(bar)
        : formatCompactNumber(bar);
      labelWidth = Math.max(labelWidth, text.length * 8);
    }
  }
  return labelWidth ? Math.max(160, (points.length - 1) * (labelWidth + 4)) : 0;
};

type LabelBox = { left: number; top: number; width: number; height: number };
const overlaps = (a: LabelBox, b: LabelBox) =>
  a.left < b.left + b.width + 2 &&
  a.left + a.width + 2 > b.left &&
  a.top < b.top + b.height + 2 &&
  a.top + a.height + 2 > b.top;

/** Edge labels can extend inward; move only colliding labels above/below their point. */
export const growthUserLabelLayout = (
  anchors: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>,
  plot: { width: number; height: number },
) => {
  const placed: LabelBox[] = [];
  return anchors.map((anchor) => {
    if (!anchor) {
      return null;
    }
    const left = Math.max(
      0,
      Math.min(plot.width - anchor.width, anchor.x - anchor.width / 2),
    );
    const preferred = anchor.y - anchor.height - 12;
    const candidates = [
      preferred,
      ...Array.from(
        { length: 10 },
        (_, i) =>
          preferred +
          (i % 2 ? 1 : -1) * (Math.floor(i / 2) + 1) * (anchor.height + 4),
      ),
    ];
    const top =
      candidates.find(
        (y) =>
          y >= 4 &&
          y + anchor.height <= plot.height &&
          !placed.some((box) =>
            overlaps(box, {
              left,
              top: y,
              width: anchor.width,
              height: anchor.height,
            }),
          ),
      ) ?? Math.max(4, preferred);
    const box = { left, top, width: anchor.width, height: anchor.height };
    placed.push(box);
    return box;
  });
};

/** Place a bar's number clear of the user-count label at the same date. */
export const growthPaidLabel = (
  barTop: number,
  bottom: number,
  users: { top: number; bottom: number } | null,
) => {
  const height = 16;
  const candidates = [
    barTop + 8,
    barTop - height - 6,
    bottom - height - 6,
    ...(users ? [users.top - height - 8, users.bottom + 8] : []),
  ];
  const top =
    candidates.find(
      (value) =>
        value >= 4 &&
        value + height <= bottom &&
        (!users ||
          value + height + 6 <= users.top ||
          value >= users.bottom + 6),
    ) ?? 4;
  return { top, inside: top >= barTop && top + height <= bottom };
};
